import type { JSX } from "react";

export type HeadScriptDescriptor = JSX.IntrinsicElements["script"];

const SCRIPT_TAG_PATTERN = /<script\b([^>]*?)\/?>([\s\S]*?)<\/script>/gi;
const ATTRIBUTE_PATTERN =
  /([^\s=/>"']+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;

// React prop names for the attributes React does not accept in lowercase.
const REACT_ATTRIBUTE_NAMES: Record<string, string> = {
  crossorigin: "crossOrigin",
  referrerpolicy: "referrerPolicy",
  nomodule: "noModule",
  fetchpriority: "fetchPriority",
};

function parseAttributes(rawAttributes: string) {
  const attributes: Record<string, string | boolean> = {};

  for (const match of rawAttributes.matchAll(ATTRIBUTE_PATTERN)) {
    const name = match[1].toLowerCase();
    const value = match[2] ?? match[3] ?? match[4];
    attributes[REACT_ATTRIBUTE_NAMES[name] ?? name] = value ?? true;
  }

  return attributes;
}

/**
 * Turns the tenant-provided head_script value into TanStack Router head()
 * script descriptors. Accepts full HTML snippets (e.g. a Google Analytics
 * tag with one or more <script> tags) as well as bare JavaScript.
 */
export function parseHeadScript(headScript: string): HeadScriptDescriptor[] {
  const trimmed = headScript.trim();
  if (!trimmed) return [];

  const descriptors: HeadScriptDescriptor[] = [];

  for (const match of trimmed.matchAll(SCRIPT_TAG_PATTERN)) {
    const descriptor = parseAttributes(match[1]) as HeadScriptDescriptor;
    const code = match[2].trim();
    if (code) descriptor.children = code;
    if (descriptor.src ?? descriptor.children) descriptors.push(descriptor);
  }

  // Content without <script> tags is treated as bare JavaScript, unless it
  // looks like other HTML (e.g. a stray meta tag), which scripts can't carry.
  if (descriptors.length === 0 && !trimmed.startsWith("<")) {
    return [{ children: trimmed }];
  }

  return descriptors;
}
